import React, { useState, useEffect } from 'react';

interface TesterProfile {
  id: string;
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    location: string;
    joinedDate: string;
  };
  verification: {
    idVerified: boolean;
    idVerificationDate: string;
    backgroundCheckPassed: boolean;
    emailVerified: boolean;
    phoneVerified: boolean;
  };
  kratomExperience: {
    yearsUsing: string;
    experienceLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
    primaryReasons: string[];
    favoriteStrains: string[];
    dailyUsage: string;
    toleranceLevel: 'low' | 'medium' | 'high';
  };
  testerStats: {
    reviewsCompleted: number;
    averageRating: number;
    averageResponseTime: number; // hours
    verifiedSamplesReceived: number;
    totalWordsWritten: number;
    specialtyAreas: string[];
    trustScore: number;
  };
  preferences: {
    maxRequestsPerMonth: number;
    preferredStrainTypes: string[];
    availableForRushRequests: boolean;
    communicationPreference: 'email' | 'sms' | 'both';
    shippingAddress: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
    };
  };
  earnings: {
    totalEarned: number;
    currentBalance: number;
    reviewBonuses: number;
    qualityBonuses: number;
    paymentMethod: string;
  };
  badges: TesterBadge[];
  recentActivity: TesterActivity[];
}

interface TesterBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  earnedDate: string;
  category: 'verification' | 'quality' | 'volume' | 'expertise' | 'community';
}

interface TesterActivity {
  id: string;
  type: 'review_completed' | 'sample_received' | 'request_accepted' | 'badge_earned' | 'payment_received';
  description: string;
  date: string;
  metadata?: any;
}

interface TrustedTesterProfileProps {
  testerId: string;
  isOwnProfile?: boolean;
}

export default function TrustedTesterProfile({ testerId, isOwnProfile = false }: TrustedTesterProfileProps) {
  const [profile, setProfile] = useState<TesterProfile | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'settings' | 'earnings' | 'activity'>('overview');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      
      setTimeout(() => {
        const mockProfile: TesterProfile = {
          id: testerId,
          personalInfo: {
            firstName: 'Sarah',
            lastName: 'Martinez',
            email: 'sarah.m@email.com',
            phone: '+1 (555) 123-4567',
            location: 'San Diego, CA',
            joinedDate: '2023-08-15'
          },
          verification: {
            idVerified: true,
            idVerificationDate: '2023-08-16',
            backgroundCheckPassed: true,
            emailVerified: true,
            phoneVerified: true
          },
          kratomExperience: {
            yearsUsing: '5+',
            experienceLevel: 'expert',
            primaryReasons: ['Pain Management', 'Sleep Aid', 'Anxiety Relief'],
            favoriteStrains: ['Red Maeng Da', 'Red Bali', 'Green Malaysian'],
            dailyUsage: '3-4g morning, 4-5g evening',
            toleranceLevel: 'medium'
          },
          testerStats: {
            reviewsCompleted: 47,
            averageRating: 4.9,
            averageResponseTime: 12,
            verifiedSamplesReceived: 52,
            totalWordsWritten: 23400,
            specialtyAreas: ['Red Strains', 'Pain Management', 'Quality Analysis'],
            trustScore: 98
          },
          preferences: {
            maxRequestsPerMonth: 8,
            preferredStrainTypes: ['Red Maeng Da', 'Red Bali', 'Green Malaysian'],
            availableForRushRequests: true,
            communicationPreference: 'email',
            shippingAddress: {
              street: '123 Main St',
              city: 'San Diego',
              state: 'CA',
              zipCode: '92101'
            }
          },
          earnings: {
            totalEarned: 2340,
            currentBalance: 180,
            reviewBonuses: 940,
            qualityBonuses: 450,
            paymentMethod: 'PayPal'
          },
          badges: [
            {
              id: '1',
              name: 'Expert Reviewer',
              description: 'Completed 25+ high-quality reviews',
              icon: '🎓',
              earnedDate: '2024-01-01',
              category: 'expertise'
            },
            {
              id: '2',
              name: 'Quality Writer',
              description: 'Consistently detailed and helpful reviews',
              icon: '✍️',
              earnedDate: '2023-12-15',
              category: 'quality'
            },
            {
              id: '3',
              name: 'Fast Responder',
              description: 'Average response time under 24 hours',
              icon: '⚡',
              earnedDate: '2023-11-20',
              category: 'quality'
            },
            {
              id: '4',
              name: 'Verified Tester',
              description: 'Completed full verification process',
              icon: '✅',
              earnedDate: '2023-08-16',
              category: 'verification'
            }
          ],
          recentActivity: [
            {
              id: '1',
              type: 'review_completed',
              description: 'Completed review for Premium Red Maeng Da',
              date: '2024-01-20'
            },
            {
              id: '2',
              type: 'sample_received',
              description: 'Received sample: Green Thai Kratom',
              date: '2024-01-18'
            },
            {
              id: '3',
              type: 'payment_received',
              description: 'Payment received: $50.00',
              date: '2024-01-15'
            },
            {
              id: '4',
              type: 'request_accepted',
              description: 'Accepted testing request from Premium Kratom Co',
              date: '2024-01-12'
            }
          ]
        };

        setProfile(mockProfile);
        setLoading(false);
      }, 1000);
    };

    fetchProfile();
  }, [testerId]);

  const getBadgeColor = (category: string) => {
    switch (category) {
      case 'verification': return '#10b981';
      case 'quality': return '#3b82f6';
      case 'volume': return '#f59e0b';
      case 'expertise': return '#8b5cf6';
      case 'community': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'review_completed': return '📝';
      case 'sample_received': return '📦';
      case 'request_accepted': return '✅';
      case 'badge_earned': return '🏆';
      case 'payment_received': return '💰';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #f3f3f3', borderTop: '3px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Profile not found</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Profile Header */}
      <div style={{ backgroundColor: '#2d3748', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              backgroundColor: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '2rem',
              fontWeight: 'bold'
            }}>
              {profile.personalInfo.firstName.charAt(0)}{profile.personalInfo.lastName.charAt(0)}
            </div>
            <div>
              <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: '0 0 0.5rem 0', color: '#1f2937' }}>
                {profile.personalInfo.firstName} {profile.personalInfo.lastName.charAt(0)}.
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                <span style={{ 
                  fontSize: '0.875rem', 
                  padding: '0.25rem 0.75rem', 
                  backgroundColor: '#3b82f620',
                  color: '#3b82f6',
                  borderRadius: '0.25rem',
                  fontWeight: '500',
                  textTransform: 'capitalize'
                }}>
                  {profile.kratomExperience.experienceLevel} Tester
                </span>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  📍 {profile.personalInfo.location}
                </span>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  📅 Joined {new Date(profile.personalInfo.joinedDate).toLocaleDateString()}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {profile.verification.idVerified && (
                  <span style={{ color: '#10b981', fontSize: '0.875rem' }}>✅ ID Verified</span>
                )}
                <span style={{ color: '#059669', fontSize: '0.875rem', fontWeight: '600' }}>
                  Trust Score: {profile.testerStats.trustScore}%
                </span>
              </div>
            </div>
          </div>
          
          {isOwnProfile && (
            <button
              onClick={() => setEditing(!editing)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              {editing ? 'Save Changes' : 'Edit Profile'}
            </button>
          )}
        </div>

        {/* Stats Row */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '1.5rem' 
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>{profile.testerStats.reviewsCompleted}</div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Reviews Completed</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>★ {profile.testerStats.averageRating}</div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Average Rating</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>{profile.testerStats.averageResponseTime}h</div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Avg Response Time</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>${profile.earnings.totalEarned}</div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Earned</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ borderBottom: '2px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '2rem' }}>
            {(['overview', 'settings', 'earnings', 'activity'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '0.75rem 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === tab ? '#3b82f6' : '#6b7280',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
          {/* Badges */}
          <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>
              Badges & Achievements
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
              {profile.badges.map(badge => (
                <div key={badge.id} style={{ 
                  padding: '1rem', 
                  textAlign: 'center',
                  border: `2px solid ${getBadgeColor(badge.category)}20`,
                  borderRadius: '0.5rem',
                  backgroundColor: `${getBadgeColor(badge.category)}10`
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{badge.icon}</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                    {badge.name}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {new Date(badge.earnedDate).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Specialties */}
          <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>
              Kratom Experience
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Primary Uses
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {profile.kratomExperience.primaryReasons.map(reason => (
                    <span key={reason} style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem', 
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      borderRadius: '0.25rem'
                    }}>
                      {reason}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Favorite Strains
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {profile.kratomExperience.favoriteStrains.map(strain => (
                    <span key={strain} style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem', 
                      backgroundColor: '#3b82f620',
                      color: '#3b82f6',
                      borderRadius: '0.25rem'
                    }}>
                      {strain}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Usage Pattern
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {profile.kratomExperience.dailyUsage}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'earnings' && isOwnProfile && (
        <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1f2937' }}>
            Earnings Overview
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>${profile.earnings.totalEarned}</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Earned</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>${profile.earnings.currentBalance}</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Current Balance</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>${profile.earnings.reviewBonuses}</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Review Bonuses</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>${profile.earnings.qualityBonuses}</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Quality Bonuses</div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Payment Method</div>
              <div style={{ fontSize: '1rem', fontWeight: '500', color: '#1f2937' }}>{profile.earnings.paymentMethod}</div>
            </div>
            <button style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}>
              Request Payout
            </button>
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div style={{ backgroundColor: '#2d3748', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: '#1f2937' }}>
            Recent Activity
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {profile.recentActivity.map(activity => (
              <div key={activity.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '1rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '0.375rem'
              }}>
                <div style={{ fontSize: '1.5rem' }}>{getActivityIcon(activity.type)}</div>
                <div style={{ flex: '1' }}>
                  <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>{activity.description}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {new Date(activity.date).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}